/* eslint max-lines: ["error", {"max": 350, "skipBlankLines": true, "skipComments": true }] */
import Registry from 'core/ComponentRegistry';
import { Event } from 'services/EventEmitter';
import Component from 'core/Component';
import { on, off, trigger } from 'toolbox/event';
import { debounce } from 'toolbox/debounce';
import { deepMerge } from 'toolbox/deepMerge';
import { mix } from 'core/mixwith';
import Accessibility from 'mixins/Accessibility';

/**
 * This is a description of the Swatches constructor function.
 * @class
 * @classdesc This is a description of the Swatches class.
 * @extends Component
 */
export default class Swatches extends mix(Component).with(Accessibility) {
    /**
     * @typedef {Object} optionsObject
     * @property {String} dataModelId - the data model name needed to render the component on client side.
     * @property {Boolean} reloadOnProductUpdate - if true, this component will be reloaded on "product.updated" event.
     *                                             Please refer product/Product component for more info.
     * @property {Boolean} changeImageOnHover - if true, then trigger an event on hover on swatch to switch product image
     * @property {Boolean} changeImageOnSelectSwatch - if true, then trigger an event on select swatch to switch product image
     * @property {Object} classNames
     */

    /**
     * @constructor
     * @param {HTMLElement} element Component DOM node
     * @param {optionsObject} options - Component Options
     */
    constructor(element, options = {}) {
        super(element, deepMerge({
            templateName: 'product/swatches',
            dataModelId: 'swatches',
            reloadOnProductUpdate: false,
            changeImageOnHover: false,
            changeImageOnSelectSwatch: true, // if true, then trigger an event on select swatch to switch product image
            classNames: {
                selected: 'm-selected',
            },
            shadesContentUrl: null,
            cssPath: 'shade-selector.css',
            containerDataModelId: 'variationselection',
            preventClickOnDisabled: false,
            trackFocusedElements: true, // enable tracking of focused elements
        }, options));
    }

    /**
     * All selectors must be cached. Never cache elements that are out of the component scope
     */
    initCache() {
        this.selectors.swatches = this.element.querySelectorAll('[data-js-swatch]');
        this.selectors.selectedSwatches = this.element.querySelectorAll('[data-js-selected]');
        this.selectors.carousel = this.element.querySelector('.c-carousel');
        this.selectors.toggleControl = this.element.querySelector('[data-js-swatches-toggle-control]');
        this.selectors.viewAll = this.element.querySelector('[data-js-swatches-view-all]');
        this.selectors.viewAllLink = this.element.querySelector('[data-js-swatches-view-all-link]');
        this.selectors.selectedShade = this.element.querySelector('[data-js-shade-selected]');

        this.classes = {
            hidden: 'h-hidden',
        };
    }

    /**
     * Init the different state of the component
     * It helps to avoid heavy DOM manipulation
     */
    initState() {
        const { selectedSwatches } = this.selectors;
        if (selectedSwatches && selectedSwatches.length) {
            this.state.originalImage = selectedSwatches[0].getAttribute('data-js-productimgsrc');
            this.state.selectedPid = selectedSwatches[0].getAttribute('data-js-pid');
        }
        this.state.loading = false;
        this.state.expanded = false;
        this.state.pid = this.options.pid;
        this.state.uuid = this.options.uuid;
    }

    /**
     * Should contain only event listeners and nothing else
     * All the event handlers should be into a separated function. No usage of anonymous function
     */
    bindEvents() {
        super.bindEvents();

        on('click', this.selectors.swatches, e => this.onSwatchClick(e));
        on('keydown', this.selectors.swatches, this.onSwatchKeyboardControl.bind(this));

        on('product.updated', this.element, this.onProductUpdated.bind(this));

        if (this.options.changeImageOnHover) {
            on('mouseover', this.element, debounce(this.onMouseOver.bind(this)));
            on('mouseout', this.element, debounce(this.onMouseOut.bind(this)));
        }

        if (this.selectors.carousel) {
            on('carousel.updated', this.element, this.onCarouselUpdated.bind(this));
        }

        on('swatches.update', this.element, this.onSwatchesUpdate.bind(this));

        if (this.selectors.viewAllLink) {
            on('click', this.selectors.viewAllLink, this.onViewAllLinkClick.bind(this));
        }
        if (this.selectors.toggleControl) {
            on('click', this.selectors.toggleControl, this.onToggleMoreShadesClick.bind(this));
        }
    }

    /**
     * After init
     * Run any script after the component is fully initialized
     */
    afterInit() {
        this.restoreLastFocusState();

        // Triggers the 'carousel.updated' event in case if the Carousel class has alredy been initiliazed
        // prior to the Swatches class to initlize swatches on page loaded
        if (this.selectors.carousel) {
            Registry.afterRegistered(this.selectors.carousel).then(() => {
                trigger('carousel.updated', this.element, {
                    bubbles: true,
                });
            });
        }
    }

    /**
     * 'swatches.update' event handler
     * @param {Object} event event data
     */
    onSwatchesUpdate(event) {
        const { detail } = event;

        this.swatchUpdate(detail);
    }

    /**
     * Updates swatches
     * @param {Object} detail event details
     * @param {Boolean} isProductUpdated if true, gets data from product updated event
     */
    swatchUpdate(detail, isProductUpdated = false) {
        this.state.loading = false;
        // do not update swatches if this component is an initiator of product update.
        if (detail.source === this.id) {
            return;
        }

        // if the same variation ID updated, no need to reload an entire component.
        // Only selected swatch needs to be changed
        if (detail.variationAttributeId === this.options.variationAttributeId) {
            this.selectSwatch(detail.pid);
        } else {
            const model = this.getModel(isProductUpdated ? detail.data : detail);
            if (model) {
                this.render(model);
            }
        }
    }

    /**
     * Set loading state to false
     * Can be used in case if no need to reload component after selection
     */
    stopLoading() {
        this.state.loading = false;
    }

    /**
     * Update current component if other variation attribute is changed.
     *
     * @param {Object} event event data
     */
    onProductUpdated(event) {
        const { detail } = event;

        this.swatchUpdate(detail, true);
    }

    /**
     * Find model for swatch by dataModelId
     * @param {Object} data array of components models
     * @returns {Object|null} model object
     */
    getModel(data) {
        const { containerDataModelId } = this.options;

        if (data) {
            if (data[containerDataModelId] && data[containerDataModelId][this.options.dataModelId]) {
                return data[containerDataModelId][this.options.dataModelId];
            }

            if (data[this.options.dataModelId]) {
                return data[this.options.dataModelId];
            }
        }

        return null;
    }

    /**
     * Click event handler
     *
     * @param {Object} e - Event object
     */
    onSwatchClick(e) {
        const targetElement = e.currentTarget;

        e.preventDefault();
        if (targetElement.classList.contains(this.options.classNames.selected) || this.state.loading) {
            return;
        }

        // prevent click for disabled swatches
        if (this.options.preventClickOnDisabled && targetElement.classList.contains('m-disabled')) {
            return;
        }

        this.selectSwatch(targetElement.getAttribute('data-js-pid'));
        this.update(targetElement);
    }

    /**
     * Keydown event handler
     *
     * @param {Object} event Event object
     */
    onSwatchKeyboardControl(event) {
        if (['Enter', ' '].indexOf(event.key) !== -1) {
            event.preventDefault();
            this.onSwatchClick(event);
        }
    }

    /**
     * Mouse over event handler
     *
     * @param {Object} e - Event object
     */
    onMouseOver(e) {
        // find swatch element from event target
        const swatch = e.target.matches('[data-js-swatch]') ? e.target : e.target.closest('[data-js-swatch]');

        if (!swatch || swatch.classList.contains(this.options.classNames.selected)) {
            return;
        }

        const newImage = swatch.getAttribute('data-js-productimgsrc');
        this.updateImage(newImage, false);
    }

    /**
     * Mouse out event handler
     *
     * @param {Object} e - Event object
     */
    onMouseOut(e) {
        // find swatch element from event target
        const swatch = e.target.matches('[data-js-swatch]') ? e.target : e.target.closest('[data-js-swatch]');

        if (!swatch) {
            return;
        }

        this.updateImage(this.state.originalImage, true);
    }

    /* eslint-disable complexity */
    /**
     * Marks target swatch as selected.
     * @param {String} pid - swatch product ID to select
     */
    selectSwatch(pid) {
        const selectedSwatches = this.element.querySelectorAll(`.${this.options.classNames.selected}`);
        if (selectedSwatches) {
            selectedSwatches.forEach((swatch) => {
                swatch.classList.remove(this.options.classNames.selected);
                swatch.removeAttribute('data-js-selected');
                if (swatch.querySelector('[data-js-selected-text]')) {
                    swatch.querySelector('[data-js-selected-text]').setAttribute('aria-hidden', 'true');
                }
                swatch.removeAttribute('aria-live');
                swatch.removeAttribute('aria-relevant');
            });
        }
        this.selectors.selectedSwatches = this.element.querySelectorAll(`[data-js-pid="${pid}"]`);
        this.selectors.selectedSwatches.forEach((swatch) => {
            swatch.classList.add(this.options.classNames.selected);
            swatch.setAttribute('data-js-selected', '');
            if (swatch.querySelector('[data-js-selected-text]')) {
                swatch.querySelector('[data-js-selected-text]').setAttribute('aria-hidden', 'false');
            }
            swatch.setAttribute('aria-live', 'polite');
            swatch.setAttribute('aria-relevant', 'text');
        });

        if (this.selectors.selectedSwatches && this.selectors.selectedSwatches.length) {
            const selectedSwatch = this.selectors.selectedSwatches[0];
            this.state.originalImage = selectedSwatch.getAttribute('data-js-productimgsrc');
            this.state.previousPid = this.state.selectedPid;
            this.state.selectedPid = pid;
            if (this.options.changeImageOnSelectSwatch) {
                this.updateImage(this.state.originalImage, true);
            }
            if (this.selectors.carousel && !this.state.expanded) {
                let index = Number(selectedSwatch.parentElement.getAttribute('data-index'));

                // Prevent the selected swatch overlapping with arrow
                if (this.options.isArrowsInside && index !== 0) {
                    index -= 1;
                }
                this.scrollToSwatch(index);
            }

            if (this.selectors.selectedShade) {
                this.selectors.selectedShade.innerHTML = this.selectors.selectedSwatches[0].getAttribute('data-js-title');
            }
        }
    }
    /* eslint-enable complexity */

    /**
     * Returns component key
     * @override from Analytics
     * @returns {String} component key
     */
    getComponentKey() {
        return `${super.getComponentKey()}$${this.state.pid}`;
    }

    /**
     * Fire event to update product variation.
     * @param {HTMLElement} targetElement - selected swatch
     */
    update(targetElement) {
        if (this.state.loading) {
            return;
        }
        const previousPid = this.state.pid || this.state.previousPid;
        this.state.pid = targetElement.getAttribute('data-js-pid');
        this.saveLastFocusState();
        const data = {
            previousPid,
            pid: this.state.pid,
            url: targetElement.href,
            source: this.id,
            variationAttributeId: this.options.variationAttributeId,
            displayValue: targetElement.getAttribute('data-js-title'),
            value: targetElement.getAttribute('data-js-value'),
            upc: targetElement.getAttribute('data-js-upc'),
            backgroundColor: targetElement.style.backgroundColor,
            backgroundImage: targetElement.style.backgroundImage,
            onUpdate: () => this.stopLoading(),
            bubbles: true,
        };

        this.state.loading = true;

        trigger('swatch.change', this.element, data);
        trigger('product.variation.update', this.element, data);
    }

    /**
     * Updating a producttile image
     *
     * @param {String} imageUrl - Image url string
     * @param {Boolean} isRestoreOriginal - if true, than image should be restored to the original one.
     */
    updateImage(imageUrl, isRestoreOriginal) {
        trigger('product.image.update', this.element, { bubbles: true, imageUrl, isRestoreOriginal });
    }

    /**
     * Carousel updated event handler
     * Check carousel state and hide "More Shades" or "See All Shades" toggle if all shades are visible and carousel is disabled
     */
    onCarouselUpdated() {
        if (this.selectors.toggleControl) {
            if (this.selectors.carousel.classList.contains('m-disabled')) {
                this.selectors.toggleControl.classList.add(this.classes.hidden);
            } else {
                this.selectors.toggleControl.classList.remove(this.classes.hidden);
            }
        }

        if (this.selectors.viewAll) {
            if (this.selectors.carousel.classList.contains('m-disabled')) {
                this.selectors.viewAll.classList.add(this.classes.hidden);
            } else {
                this.selectors.viewAll.classList.remove(this.classes.hidden);
            }
        }
    }

    /**
     * Scrolling to the selected swatch
     *
     * @param {Number} index - index of the selected swatch
     */
    scrollToSwatch(index) {
        trigger('carousel.scrollto', this.selectors.carousel, { index });
    }

    /**
     * 'ViewAllLink' click event handler
     */
    onViewAllLinkClick() {
        if (this.state.selectedPid) {
            Event.emit('modal.open', {
                options: {
                    cssPath: this.options.cssPath,
                    contentUrl: `${this.options.shadesContentUrl}?pid=${this.state.selectedPid}`,
                    beforeClose: this.beforeShadeSelectorClose.bind(this),
                    classNames: { container: ['m-shade-selector'] },
                    enableStacking: true,
                    analytics: {
                        virtualPageURL: this.analytics.virtualPageURL,
                        virtualPageTitle: this.analytics.virtualPageTitle,
                    },
                },
            });
        }
    }

    /**
     * 'More Shades' click event handler
     */
    onToggleMoreShadesClick() {
        if (this.state.expanded) {
            this.unexpandSwatches();
        } else {
            this.expandSwatches();
        }
    }

    /**
     * Expand the swatches carousel
     */
    expandSwatches() {
        const carousel = Registry.getComponent(this.selectors.carousel);
        this.selectors.toggleControl.classList.add('m-active');
        this.selectors.toggleControl.setAttribute('aria-expanded', 'true');
        carousel.expandSlides();
        this.state.expanded = true;
    }

    /**
     * Unexpand the swatches carousel
     */
    unexpandSwatches() {
        const carousel = Registry.getComponent(this.selectors.carousel);
        this.selectors.toggleControl.classList.remove('m-active');
        this.selectors.toggleControl.setAttribute('aria-expanded', 'false');
        carousel.unexpandSlides();
        this.state.expanded = false;

        // if a swatch is selected, display the carousel view where the swatch is
        const { selectedSwatches } = this.selectors;
        if (selectedSwatches && selectedSwatches.length) {
            const selectedSwatchCarouselItem = selectedSwatches[0].closest('[data-js-carousel-item]');
            if (selectedSwatchCarouselItem) {
                const selectedSwatchCarouselItemIndex = Number(selectedSwatchCarouselItem.getAttribute('data-index') || 0);
                carousel.goTo(selectedSwatchCarouselItemIndex, 500);
            }
        }
    }

    /**
     * ShadeSelector model beforeClose callback handler
     *
     * @param {HTMLElement} element Component DOM node
     * @param {Object} options - object with params
     */
    beforeShadeSelectorClose(element, options) {
        const selected = element.querySelector('[data-js-selected]');

        if (selected && options.closeActionTargetType === 'submit') {
            const previousPid = this.state.pid || this.state.previousPid;
            this.state.pid = selected.getAttribute('data-js-pid');
            const data = {
                previousPid,
                pid: this.state.pid,
                url: selected.href,
                variationAttributeId: this.options.variationAttributeId,
                displayValue: selected.getAttribute('title'),
                value: selected.getAttribute('data-js-value'),
                bubbles: true,
            };

            if (this.state.uuid) {
                data.uuid = this.state.uuid;
            }

            trigger('product.variation.update', this.element, data);
        }
    }

    /**
     * Unbind event handlers
     */
    unbindEvents() {
        off('click keydown', this.selectors.swatches);

        if (this.options.changeImageOnHover) {
            off('mouseover', this.element);
            off('mouseout', this.element);
        }

        off('product.updated', this.element);
        off('swatches.update', this.element);

        if (this.selectors.viewAllLink) {
            off('click', this.selectors.viewAllLink);
        }

        if (this.selectors.carousel) {
            off('carousel.updated', this.element);
        }

        if (this.selectors.toggleControl) {
            off('click', this.selectors.toggleControl);
        }
    }

    /**
     * Destroy is called automatically after the component is being removed from the DOM
     * You must always destroy the listeners attached to an element to avoid any memory leaks
     */
    destroy() {
        super.destroy();
        this.unbindEvents();
    }
}
